/**
 * CompressorModule — built around DynamicsCompressorNode. Threshold,
 * ratio, attack, release are direct AudioParams; Makeup is a post-comp
 * gain stage so the compressed signal can be brought back to unity.
 *
 * Graph:
 *
 *   input → compressor → makeupGain → output
 *
 * No dry/wet — compression is almost always 100% wet; if you want
 * parallel compression you'd run a mod connection on Threshold or use
 * a second effect instance (once multi-instance lands).
 */

import type { AudioEngine } from '../AudioEngine';
import { NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

export class CompressorModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'compressor' as const;
  readonly displayName = 'COMP';
  readonly id: string;
  bypass = false;

  readonly threshold = new NumericParameter({
    name: 'Threshold',
    default: -24,
    min: -60,
    max: 0,
    curve: 'linear',
    unit: 'dB',
    smoothingMs: 20,
    formatter: (v) => `${v.toFixed(0)}`,
  });
  readonly ratio = new NumericParameter({
    name: 'Ratio',
    default: 4,
    min: 1,
    max: 20,
    curve: 'linear',
    smoothingMs: 20,
    formatter: (v) => `${v.toFixed(1)}:1`,
  });
  readonly attack = new NumericParameter({
    name: 'Attack',
    default: 0.01,
    min: 0.001,
    max: 0.5,
    curve: 'exp',
    unit: 's',
    smoothingMs: 20,
    formatter: (v) => `${(v * 1000).toFixed(0)}ms`,
  });
  readonly release = new NumericParameter({
    name: 'Release',
    default: 0.2,
    min: 0.01,
    max: 2,
    curve: 'exp',
    unit: 's',
    smoothingMs: 20,
    formatter: (v) => `${(v * 1000).toFixed(0)}ms`,
  });
  readonly makeup = new NumericParameter({
    name: 'Makeup',
    default: 1,
    min: 0,
    max: 3,
    curve: 'linear',
    smoothingMs: 20,
    // Small random skew — randomize shouldn't push makeup gain very high.
    randomSkew: 0.5,
    formatter: (v) => `${v.toFixed(2)}×`,
  });

  readonly parameters: readonly AnyParameter[] = [
    this.threshold,
    this.ratio,
    this.attack,
    this.release,
    this.makeup,
  ];

  private inputNode: GainNode | null = null;
  private outputNode: GainNode | null = null;
  private comp: DynamicsCompressorNode | null = null;
  private makeupGain: GainNode | null = null;
  private started = false;

  constructor(id?: string) {
    this.id = id ?? mintId('comp');
  }

  get input(): AudioNode | null {
    return this.inputNode;
  }
  get output(): AudioNode | null {
    return this.outputNode;
  }

  dispose(): void {
    try {
      this.inputNode?.disconnect();
      this.outputNode?.disconnect();
      this.comp?.disconnect();
      this.makeupGain?.disconnect();
    } catch {
      /* already disconnected */
    }
    this.started = false;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    const ctx = engine.context;
    this.inputNode = ctx.createGain();
    this.outputNode = ctx.createGain();
    this.comp = ctx.createDynamicsCompressor();
    this.makeupGain = ctx.createGain();
    this.makeupGain.gain.value = this.makeup.value;

    this.inputNode.connect(this.comp);
    this.comp.connect(this.makeupGain);
    this.makeupGain.connect(this.outputNode);

    this.threshold.bindAudioParam(this.comp.threshold, ctx);
    this.ratio.bindAudioParam(this.comp.ratio, ctx);
    this.attack.bindAudioParam(this.comp.attack, ctx);
    this.release.bindAudioParam(this.comp.release, ctx);
    this.makeup.bindAudioParam(this.makeupGain.gain, ctx);

    this.started = true;
  }

  /** Macro routing — Threshold is the most musical target for automation. */
  getAudioParam(paramName: string): AudioParam | null {
    if (paramName === 'Threshold') return this.comp?.threshold ?? null;
    if (paramName === 'Ratio') return this.comp?.ratio ?? null;
    if (paramName === 'Makeup') return this.makeupGain?.gain ?? null;
    return null;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    readParamState(this.parameters, state.params, 'preset');
  }
}
